import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useStore } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import {
  readWorkingView, workingViewAuthScope, workingViewStore, writeWorkingView,
  type WorkingViewAddress, type WorkingViewDefinition,
} from '@/stores/workingViewStore';

export interface ScrollAnchor { id: string | null; offset: number; left: number }
/** Read current sticky geometry only when capturing/restoring, never on feed renders. */
export type ScrollOcclusion = number | ((root: HTMLElement) => number);
function occludedHeight(root: HTMLElement, occlusion: ScrollOcclusion): number {
  const value = typeof occlusion === 'function' ? occlusion(root) : occlusion;
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/** Only chrome covering the top reading edge counts. A bottom-docked strip
 * must not be added to a top toolbar, and overlapping sticky bands count once. */
export function measureTopOcclusion(root: HTMLElement, elements: readonly HTMLElement[]): number {
  const bounds = root.getBoundingClientRect();
  let bottom = bounds.top;
  const bands = elements.map(el => el.getBoundingClientRect())
    .filter(r => r.height > 0 && r.right > bounds.left && r.left < bounds.right)
    .sort((a, b) => a.top - b.top);
  for (const band of bands) {
    if (band.top <= bottom + 1 && band.bottom > bottom) bottom = Math.min(bounds.bottom, band.bottom);
  }
  return Math.max(0, bottom - bounds.top);
}
const definition: WorkingViewDefinition<ScrollAnchor> = {
  id: 'pane-scroll-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<ScrollAnchor>;
    if (!(v.id === null || (typeof v.id === 'string' && v.id.length <= 512))
      || !Number.isFinite(v.offset) || !Number.isFinite(v.left)) return null;
    return { id: v.id, offset: Math.max(-100000, Math.min(100000, v.offset!)), left: Math.max(0, Math.min(100000, v.left!)) };
  },
};

/** Adapter returns stable item identities, not cached DOM nodes or row indices. */
export interface WorkingScrollOptions {
  pane: string;
  instance: string;
  context: string;
  ready: boolean;
  occlusion?: ScrollOcclusion;
  enabled?: boolean;
  /** As `useWorkingView`: false = a new visit starts at the top. Read once per visit. */
  resumeLatest?: boolean;
  /** false: a capture equal to the held record is not rewritten, so viewing an
   *  entry (its restore, or leaving it unmoved) never makes it the latest.
   *  Default true keeps the shared behaviour (#1078 scopes this to Game Detail). */
  rewriteUnchanged?: boolean;
}
function anchorId(el: HTMLElement): string {
  const section = el.parentElement?.closest<HTMLElement>('section[data-view-anchor]');
  return JSON.stringify([section?.dataset.viewAnchor ?? null, el.dataset.viewAnchor]);
}
export function captureScrollAnchor(root: HTMLElement, occlusion: ScrollOcclusion = 0): ScrollAnchor {
  const top = root.getBoundingClientRect().top + occludedHeight(root, occlusion);
  const items = Array.from(root.querySelectorAll<HTMLElement>('[data-view-anchor]'));
  const item = items.find((el) => !el.querySelector('[data-view-anchor]') && el.getBoundingClientRect().bottom > top)
    ?? items.find((el) => el.getBoundingClientRect().bottom > top);
  return { id: item ? anchorId(item) : null,
    offset: item ? item.getBoundingClientRect().top - top : 0, left: root.scrollLeft };
}
export function restoreScrollAnchor(root: HTMLElement, anchor: ScrollAnchor, occlusion: ScrollOcclusion = 0): boolean {
  const item = Array.from(root.querySelectorAll<HTMLElement>('[data-view-anchor]'))
    .find((el) => anchorId(el) === anchor.id);
  // Deliberate: a row that is no longer rendered must not move the reader, which
  // `useWorkingScroll.test.tsx:66` pins. Note this seeks by DOM PRESENCE, so on a
  // WINDOWED list "absent" stops being rare and becomes permanent — #812 measured
  // 0/3 deep restores. #872 researched a seek-first composition in front of this
  // function (currently `src/test-utils/windowedRestore.ts`); it changes nothing here.
  if (anchor.id !== null && !item) return false;
  const requestedTop = item
    ? Math.max(0, root.scrollTop + item.getBoundingClientRect().top
      - root.getBoundingClientRect().top - occludedHeight(root, occlusion) - anchor.offset) : 0;
  root.scrollTop = requestedTop;
  root.scrollLeft = anchor.left;
  // The browser may clamp against an incompletely laid-out scroll range.
  // Re-read after writing; finding the row alone is not a completed restore.
  if (Math.abs(root.scrollTop - requestedTop) > 1 || Math.abs(root.scrollLeft - anchor.left) > 1) return false;
  // A sticky band can change position as the pane moves. Let the bounded
  // initial restore settle against its new geometry, not a latched height.
  const settledTop = item ? Math.max(0, root.scrollTop + item.getBoundingClientRect().top
    - root.getBoundingClientRect().top - occludedHeight(root, occlusion) - anchor.offset) : 0;
  return Math.abs(root.scrollTop - settledTop) <= 1;
}

/** One restore per mounted context; no feed-driven recentering or retained pages. */
export function useWorkingScroll(options: WorkingScrollOptions) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [nodeVersion, setNodeVersion] = useState(0);
  const ref = useCallback((element: HTMLDivElement | null) => {
    rootRef.current = element;
    setNodeVersion((v) => v + 1);
  }, []);
  const location = useLocation();
  const navigation = useNavigationType();
  const scope = useAuthStore((s) => s.status === 'unauthenticated' ? null : workingViewAuthScope(s.user));
  const epoch = useStore(workingViewStore, (s) => s.epoch);
  const { pane, instance, context, ready, occlusion = 0, enabled = true, resumeLatest = true, rewriteUnchanged = true } = options;
  const readyRef = useRef(ready);
  const occlusionRef = useRef(occlusion);
  const attemptRef = useRef<(() => void) | null>(null);
  const previousRef = useRef<{ visit: string; context: string; root: HTMLElement } | null>(null);
  // True only while the pane's owner unmounts (declared before the effect
  // below, so its cleanup runs first). Reset in setup for StrictMode's replay.
  const unmountingRef = useRef(false);
  useLayoutEffect(() => {
    unmountingRef.current = false;
    return () => { unmountingRef.current = true; };
  }, []);
  useLayoutEffect(() => { readyRef.current = ready; attemptRef.current?.(); }, [ready]);
  useLayoutEffect(() => { occlusionRef.current = occlusion; attemptRef.current?.(); }, [occlusion]);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !scope || !enabled) return;
    const address: WorkingViewAddress = { scope, epoch, view: definition.id,
      instance: JSON.stringify([pane, instance, context]),
      entry: JSON.stringify([location.key, location.pathname, location.search]) };
    const visit = JSON.stringify([scope, epoch, pane, instance, address.entry]);
    const prior = previousRef.current;
    const inPlaceContextChange = prior?.visit === visit && prior.root === root && prior.context !== context;
    previousRef.current = { visit, context, root };
    // A filter/density edit is not a return journey. Keep the reader where
    // they are now; the new context gets its own anchor for a later return.
    const anchor = inPlaceContextChange ? null : readWorkingView(definition, address, navigation !== 'POP' && resumeLatest);
    if (!anchor && !inPlaceContextChange) { root.scrollTop = 0; root.scrollLeft = 0; }
    let restoring = anchor !== null;
    let frame = 0;
    let restoreFrame = 0;
    let writtenPosition: { top: number; left: number } | null = null;
    let disposed = false;
    const capture = () => {
      if (!disposed && !restoring && root.clientHeight > 0) {
        const anchor = captureScrollAnchor(root, occlusionRef.current);
        // An unchanged position is not a new reading: rewriting it would make
        // a Back-restored entry the latest visit (its restore's own scroll
        // event, or its unmount) without the reader moving (#1078).
        const held = rewriteUnchanged ? null : readWorkingView(definition, address, false);
        if (held && held.id === anchor.id && Math.abs(held.offset - anchor.offset) <= 1
          && Math.abs(held.left - anchor.left) <= 1) return;
        writeWorkingView(definition, address, anchor);
      }
    };
    // Scroll events can be frequent; coalesce geometry reads only. Market and
    // execution updates are not scheduled, delayed or consumed by this hook.
    const onScroll = () => {
      // Partial browser-clamped writes also dispatch scroll. Those are not
      // reader intent; every other scroll still wins, as do explicit inputs.
      if (restoring && (!writtenPosition || Math.abs(root.scrollTop - writtenPosition.top) > 1
        || Math.abs(root.scrollLeft - writtenPosition.left) > 1)) stopRestore();
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; capture(); });
    };
    const observer = new MutationObserver(() => attempt());
    const stopRestore = () => {
      restoring = false;
      observer.disconnect();
      cancelAnimationFrame(restoreFrame);
      restoreFrame = 0;
    };
    const attempt = () => {
      if (!restoring || disposed) return;
      if (anchor && readyRef.current && root.clientHeight > 0) {
        const complete = restoreScrollAnchor(root, anchor, occlusionRef.current);
        writtenPosition = { top: root.scrollTop, left: root.scrollLeft };
        if (complete) stopRestore();
        // Layout capacity can grow without a DOM mutation (fonts, images or a
        // resized pane). Retry only within this visit's existing 1.5s window.
      }
      if (restoring && !restoreFrame) restoreFrame = requestAnimationFrame(() => {
        restoreFrame = 0;
        attempt();
      });
    };
    attemptRef.current = attempt;
    const interrupt = () => { if (restoring) stopRestore(); };
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) interrupt();
    };
    root.addEventListener('wheel', interrupt, { passive: true });
    root.addEventListener('touchstart', interrupt, { passive: true });
    root.addEventListener('pointerdown', interrupt, { passive: true });
    root.addEventListener('keydown', onKey);
    root.addEventListener('scroll', onScroll, { passive: true });
    if (restoring) observer.observe(root, { childList: true, subtree: true });
    attempt();
    // Never alter filters to recover an anchor or move the pane after the
    // reader takes over. A missing row does not justify an unrelated jump.
    const timeout = setTimeout(() => {
      if (restoring) {
        // An unreachable end-of-list anchor stays at the closest position;
        // a withdrawn row leaves the current position alone. Neither retries forever.
        stopRestore();
      }
    }, 1500);
    return () => {
      // On unmount the DOM is still this visit's, so a final capture is safe.
      // On an in-place change (another game, entry or context in the same
      // pane) React has already committed the next visit's rows; capturing now
      // would file them under this visit (#1078). Its scroll-time captures stand.
      if (unmountingRef.current) capture();
      disposed = true;
      attemptRef.current = null;
      observer.disconnect();
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(restoreFrame);
      root.removeEventListener('wheel', interrupt);
      root.removeEventListener('touchstart', interrupt);
      root.removeEventListener('pointerdown', interrupt);
      root.removeEventListener('keydown', onKey);
      root.removeEventListener('scroll', onScroll);
    };
  // `ready`/feed changes never re-arm a completed or interrupted restoration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeVersion, scope, epoch, pane, instance, context, location.key, location.pathname, location.search, enabled]);
  return ref;
}
